import apiClient from "../lib/api-client";
import type {
  Customer,
  CreateCustomerRequest,
} from "@medicorepay/shared/types";

// Re-export types for backward compatibility
export type { Customer };
export type CreateCustomerDto = CreateCustomerRequest;
export interface UpdateCustomerDto extends Partial<CreateCustomerDto> {}

function mutationHeaders() {
  return { headers: { "Idempotency-Key": crypto.randomUUID() } };
}

export const customerService = {
  async getCustomers(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{
    data: Customer[];
    total: number;
    page: number;
    limit: number;
  }> {
    const response = await apiClient.get("/customers", { params });
    return response.data;
  },

  async getCustomer(id: string): Promise<Customer> {
    const response = await apiClient.get(`/customers/${id}`);
    return response.data;
  },

  async createCustomer(data: CreateCustomerDto): Promise<Customer> {
    const response = await apiClient.post(
      "/customers",
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async updateCustomer(id: string, data: UpdateCustomerDto): Promise<Customer> {
    const response = await apiClient.patch(
      `/customers/${id}`,
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async deleteCustomer(id: string): Promise<void> {
    await apiClient.delete(`/customers/${id}`, mutationHeaders());
  },

  async getCustomerStats(id: string): Promise<{
    totalInvoices: number;
    totalPaidAmount: number;
    totalPendingAmount: number;
  }> {
    const response = await apiClient.get(`/customers/${id}/stats`);
    return response.data;
  },

  async getPaymentHistory(id: string): Promise<{
    customerPayments: any[];
    invoicePayments: any[];
  }> {
    const response = await apiClient.get(`/customers/${id}/payment-history`);
    return response.data;
  },

  async getCustomerInvoices(
    id: string,
    params?: { startDate?: string; endDate?: string },
  ): Promise<any[]> {
    const response = await apiClient.get(`/invoices`, {
      params: { customerId: id, ...params },
    });
    return response.data.data || [];
  },

  async recordPayment(
    customerId: string,
    data: {
      amount: number;
      paymentMethod: string;
      paymentDate?: string;
      allocatedInvoices?: { invoiceId: string; amount: number }[];
      reference?: string;
      notes?: string;
    },
  ) {
    const response = await apiClient.post(
      `/customers/${customerId}/payments`,
      data,
      mutationHeaders(),
    );
    return response.data;
  },

  async getCustomerPayments(
    customerId: string,
    params?: {
      page?: number;
      limit?: number;
    },
  ) {
    const response = await apiClient.get(`/customers/${customerId}/payments`, {
      params,
    });
    return response.data;
  },
};
